"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { PhoneCall, Clock, ShieldCheck, Wrench, ArrowRight, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const guarantees = [{
  title: "24/7 Rapid Response",
  subtitle: "Local technicians on standby across Jonesboro"
}, {
  title: "Upfront Pricing",
  subtitle: "No surprise fees before work starts"
}, {
  title: "Licensed & Insured",
  subtitle: "Qualified master plumbers on every call"
}];
export default function EmergencyCta() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="emergencyservicecta" data-nav="light" className="relative overflow-hidden bg-background py-16 md:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        y: 20
      }} whileInView={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="relative rounded-3xl bg-primary text-primary-foreground shadow-2xl overflow-hidden p-8 sm:p-12 lg:p-16">
          {/* Subtle background decoration */}
          <div className="absolute -right-16 -top-16 size-80 rounded-full bg-accent/10 blur-3xl pointer-events-none" />
          <div className="absolute -left-16 -bottom-16 size-80 rounded-full bg-background/5 blur-3xl pointer-events-none" />

          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            {/* Left Column: Heading & Information */}
            <div className="lg:col-span-7 space-y-6">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="secondary" className="px-3 py-1 font-semibold text-xs tracking-wide uppercase bg-accent text-accent-foreground rounded-full border-none">
                  <Clock className="size-3.5 mr-1" />
                  Immediate Dispatch Available
                </Badge>
                <span className="text-xs text-primary-foreground/80 font-medium flex items-center gap-1.5">
                  <span className="inline-block size-2 rounded-full bg-chart-4 animate-pulse" />
                  Jonesboro, GA & Surrounding Areas
                </span>
              </div>

              <div className="space-y-3">
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold tracking-tight text-primary-foreground leading-tight">
                  Facing a Plumbing Emergency Right Now?
                </h2>
                <p className="text-base sm:text-lg text-primary-foreground/90 font-sans max-w-xl">
                  Burst pipes, severe drain clogs, or overflowing water heaters can cause severe damage within minutes. Our local Jonesboro team is ready to respond immediately.
                </p>
              </div>

              {/* Trust Points */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
                {guarantees.map((item, i) => <div key={item.title} data-index={i} className="flex items-start gap-2.5 rounded-xl bg-primary-foreground/10 p-3.5 backdrop-blur-xs">
                    <CheckCircle2 className="size-5 text-accent shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-primary-foreground">
                        {item.title}
                      </p>
                      <p className="text-xs text-primary-foreground/75 leading-snug">
                        {item.subtitle}
                      </p>
                    </div>
                  </div>)}
              </div>
            </div>

            {/* Right Column: Action Card */}
            <div className="lg:col-span-5">
              <div className="rounded-2xl bg-card text-card-foreground p-6 sm:p-8 shadow-xl border border-border">
                <div className="text-center space-y-2 mb-6">
                  <div className="inline-flex p-3 rounded-full bg-primary/10 text-primary mb-1">
                    <Wrench className="size-6" />
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold font-default text-card-foreground">
                    Get Fast Local Help
                  </h3>
                  <p className="text-sm text-muted-foreground font-sans">
                    Call our dispatch center now or schedule your urgent service online.
                  </p>
                </div>

                <div className="space-y-3.5">
                  <a href="tel:4708479076" className="block w-full" aria-label="Call emergency dispatch at (470) 847-9076">
                    <Button variant="default" size="lg" className="w-full bg-accent text-accent-foreground hover:bg-accent/90 text-base font-bold py-6 shadow-md rounded-xl justify-center gap-2">
                      <PhoneCall className="size-5 animate-bounce" />
                      <span>{"(470) 847-9076"}</span>
                    </Button>
                  </a>

                  <Link to="/contact/" className="block w-full">
                    <Button variant="outline" size="lg" className="w-full text-foreground border-border hover:bg-muted font-semibold py-6 rounded-xl justify-center gap-2">
                      <span>Request Service Online</span>
                      <ArrowRight className="size-4" />
                    </Button>
                  </Link>
                </div>

                <div className="mt-5 pt-4 border-t border-border flex items-center justify-center gap-2 text-xs text-muted-foreground">
                  <ShieldCheck className="size-4 text-chart-4" />
                  <span>100% Satisfaction Guarantee on Emergency Repairs</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}